import { uniqBy } from 'lodash';
import { inject, injectable } from 'tsyringe';
import { BaseApi } from '../BaseApi';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import { MiddlewareConfig, Quote, Sdks } from '../types';
import { Watchlist, WatchlistEntry, WatchlistWithQuotes } from '../types/Watchlist.type';
import { IQuoteApi } from './interfaces/IQuoteApi';
import { IWatchlistApi } from './interfaces/IWatchlistApi';

@injectable()
export class WatchlistApi extends BaseApi implements IWatchlistApi {
    constructor(
        @inject(MiddlewareInjectToken.Sdks) sdks: Sdks,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(ApiInjectToken.QuoteApi) private quoteApi: IQuoteApi,
    ) {
        super(sdks, config);
    }

    async getAllWatchlists(): Promise<WatchlistWithQuotes[]> {
        const { data: watchlists } = await this.sdks.middleware.get<Watchlist[]>('watchlists');
        const quotes = await this.getQuotes(watchlists.flatMap((watchlist) => watchlist.entries));

        return watchlists.map((watchlist) => {
            const watchlistSymbols = watchlist.entries.map((item) => item.symbol);
            return {
                ...watchlist,
                quotes: quotes.filter((quote) => watchlistSymbols.includes(quote.symbol)),
            };
        });
    }

    async getWatchlist(watchlistId: number): Promise<WatchlistWithQuotes> {
        const { data: watchlist } = await this.sdks.middleware.get<Watchlist>('watchlists/:watchlistId', undefined, {
            pathParams: { watchlistId },
        });

        return {
            ...watchlist,
            quotes: await this.getQuotes(watchlist.entries),
        };
    }

    async getQuotes(symbols: WatchlistEntry[]): Promise<Quote[]> {
        const uniqueEntries = uniqBy(symbols, (item) => item.symbol);
        const stockSymbols = uniqueEntries.filter((item) => !item.crypto).map((item) => item.symbol);
        const cryptoSymbols = uniqueEntries.filter((item) => item.crypto).map((item) => item.symbol);
        const [stockQuotes, cryptoQuotes] = await Promise.all([
            stockSymbols.length ? this.quoteApi.getQuotes(stockSymbols, false) : Promise.resolve([]),
            cryptoSymbols.length ? this.quoteApi.getQuotes(cryptoSymbols, true) : Promise.resolve([]),
        ]);

        return [...stockQuotes, ...cryptoQuotes];
    }

    async createWatchlist(watchlist: Omit<Watchlist, 'id'>): Promise<WatchlistWithQuotes> {
        const { data: newWatchlist } = await this.sdks.middleware.post<Watchlist>('watchlists', watchlist);
        return this.getWatchlist(newWatchlist.id);
    }

    async updateWatchlist(watchlistId: number, watchlist: Partial<Watchlist>): Promise<WatchlistWithQuotes> {
        await this.sdks.middleware.put<Watchlist>('watchlists/:watchlistId', watchlist, {
            pathParams: { watchlistId },
        });
        return this.getWatchlist(watchlistId);
    }

    async deleteWatchlist(watchlistId: number): Promise<void> {
        await this.sdks.middleware.delete('watchlists/:watchlistId', undefined, { pathParams: { watchlistId } });
    }

    async addEntries(watchlistId: number, entries: WatchlistEntry[]): Promise<WatchlistWithQuotes> {
        await this.sdks.middleware.post<Watchlist>(
            'watchlists/:watchlistId/entries',
            { entries },
            { pathParams: { watchlistId } },
        );
        return this.getWatchlist(watchlistId);
    }

    async removeEntries(watchlistId: number, entries: WatchlistEntry[]): Promise<WatchlistWithQuotes> {
        await this.sdks.middleware.delete<Watchlist>(
            'watchlists/:watchlistId/entries',
            { entries },
            { pathParams: { watchlistId } },
        );
        return this.getWatchlist(watchlistId);
    }
}
